import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useEffectOnce, useForceUpdate, useInput } from "@lib-shared/hooks";
import { appSettings } from "core/config";
import { BusinessPage, ButtonText } from "core/constants";
import { AppContext } from "core/context";
import { SelfServiceContext } from "core/context/self-service.context";
import { currencyString, logApiError } from "core/lib";
import getTextWidth from "core/lib/get-text-width";
import { AmountToPayService } from "core/services/cef";
import { CustomerAccountsResponse, CustomerAccountsResponseWrapper } from "core/services/cef/amount-to-pay.service";
import PayAtService from "core/services/pay-at.service";
import UserActivityLoggingApi from "core/services/user-activity-logging.service";
import PayAtStore from "core/stores/pay-at.store";
import { PaymentAmount } from "core/stores/payment.store";
import { ShowModal, ModalAlert, ModalIcon, PleaseWait } from "components/common-ui";

const Payment = observer(({ store }: { store: PayAtStore }) => {
    const [updateMode, setUpdateMode] = useState<boolean>(false);

    const forceUpdate = useForceUpdate();

    const inputValidation = (value: string) => {
        if (value.trim().length === 0) {
            return false;
        }

        if (value.startsWith("-")) {
            return false;
        }

        const parsed = parseFloat(value);

        if (isNaN(parsed) || parsed <= 0) {
            return false;
        }

        return true;
    }

    const amountInput = useInput(inputValidation);

    const appContext = useContext(AppContext);
    const user = appContext.user;
    let _modal: any;

    const selfServiceContext = useContext(SelfServiceContext);

    const pageTitle = "- Payment";

    const navigate = useNavigate();

    const goBackToDashboard = () => navigate(BusinessPage.dashboard);

    const paymentState = {
        loading: "loading",
        loaded: "loaded",
        hidden: ""
    };

    const paymentMethods = [
        { name: "Instant EFT", className: "eft", icon: "icon-internet-payment_thin" },
        { name: "Credit Card", className: "card", icon: "icon-visa_thin" }
    ];

    const ids = {
        paymentModal: "payment-modal",
        paymentModalIFrame: "payment-modal-iframe"
    };

    //QA & Prod environments run on different ports
    const payAtEventName = "https://payat.io";

    window.addEventListener("message", event => {
        //Using includes rather than full match to reduce configuration
        if (!event.origin.startsWith(payAtEventName)) {
            return;
        }

        switch (event.data) {
            case "onLoad":
                setPaymentModalState(paymentState.loaded);
                break;

            case "onClose":
                closePaymentModal();
                break;

            default:
                break;
        }
    });

    let existingDialog: HTMLDialogElement;

    const closeModal = () => {
        if (existingDialog != null) {
            ModalAlert.close(existingDialog);
        }
    }

    const showErrorModal = (description: string, textForButton: string, title = "Error", icon = ModalIcon.error) => {
        closeModal();

        store.updateShowError(true);

        const options: any = {
            title: title,
            body: description,
            buttonText: textForButton,
            icon: icon
        };

        switch (textForButton) {
            case ButtonText.goBackToDashboard:
                options["onClose"] = () => goBackToDashboard();
                break;

            case ButtonText.tryAgain:
                options["onClose"] = () => tryAgain();
                options["showCancel"] = true;
                options["onCancel"] = () => goBackToDashboard();
                break;

            default: break;
        }

        existingDialog = ShowModal(options);
    }

    const payNow = () => {
        const pay = async () => {
            const value = parseFloat(amountInput.value);

            store.updateAmountToPayValue(value);

            if (!store.isValid) {
                return;
            }

            showLoadingModal();

            const paymentLink = await initiatePaymentRequest();

            if (!paymentLink) {
                return;
            }

            selfServiceContext.managers.amountToPay.reset();

            closeModal();

            setPaymentModalState(paymentState.loading);

            new UserActivityLoggingApi().logActivityAsync("PayAt", "Create", {
                method: paymentLink.paymentMethodName,
                amount: currencyString(store.amountToPay.amount, store.amountToPay.currencyCode)
            }); //no await

            if (store.method === "eft") {
                window.location.href = paymentLink.paymentUrl;
                return;
            }

            showPaymentModal(paymentLink.paymentUrl);
        }

        pay();
    }

    const handleAmountToPayResponse = (response: CustomerAccountsResponse) => {
        const data = new CustomerAccountsResponseWrapper(response);

        const amount = data.toPaymentAmount();

        amountInput.setValue(amount.amount.toFixed(2));

        store.updateAmountToPay(amount);

        if (!appSettings.isEnabled.overPaying && amount.amount <= 0) {
            const message = "Good news! Your account is up to date, and there is no outstanding amount for you to pay at the moment.";
            showErrorModal(message
                , ButtonText.goBackToDashboard
                , "No payment required"
                , ModalIcon.info);

            store.updateIsLoaded(true);
            return;
        }

        store.updateIsLoaded(true);

        closeModal();
    }

    const initiatePaymentRequest = async () => {
        const amount = amountInput.value;
        const amountInCents = Math.round(parseFloat(amount.toString()) * 100);

        try {
            const response = await new PayAtService().initiatePayment(amountInCents, store.method);

            return response?.data;
        } catch (error: any) {
            logApiError(error);

            const data = error?.response?.data;

            const errorTitle = data?.errorRspCode ?? "Error";
            const errorDescription = data?.errorRspDescription ?? "An error has occurred while initiating payment";

            showErrorModal(errorDescription, ButtonText.tryAgain, errorTitle);

            store.updateIsLoaded(true);

            return null;
        }
    }

    const getAccountBalance = async () => {
        try {
            const response = await new AmountToPayService().getCustomerAccounts();
            handleAmountToPayResponse(response?.data);
        } catch (error) {
            logApiError(error);

            showErrorModal("There has been an error retrieving your account balance. Please try again.", ButtonText.tryAgain);
        }
    }

    const showLoadingModal = (message = "Initialising Payment") => {
        existingDialog = ModalAlert.loading(message);
    }

    const onLoad = () => {
        showLoadingModal("Getting account details");

        appContext.updateTitle(pageTitle);
        appContext.updateHeader("Your Account");

        if (!(user?.isSouthAfricanCustomer ?? false)) {
            showErrorModal("This page is for South African customers only.", ButtonText.goBackToDashboard);
            store.updateIsLoaded(true);
            return;
        }

        getAccountBalance();
    }

    useEffectOnce(() => {
        store.reset();

        if (!user?.can.makePayment) {
            ModalAlert.permissionsError(() => navigate(BusinessPage.dashboard));
            return;
        }

        onLoad();

        return (() => {
            const modal = document.getElementById(ids.paymentModal);

            if (modal == null) {
                return;
            }

            modal.remove();
        })
    });

    const PaymentMethod = (method: any) => {
        return (
            <li className={method.className} onClick={() => selectPayment(method.className)} key={method.name} >
                <label>
                    <i className={method.icon} />
                    {method.name}
                </label>
            </li>
        );
    }

    const clearSelectedPayment = () => selectPayment(null);

    const selectPayment = (method: any) => {
        if (method == null) {
            return;
        }

        store.updateMethod(method);

        forceUpdate();
    }

    const closePaymentModal = () => {
        setPaymentModalState(paymentState.hidden);
        clearSelectedPayment();
    }

    const getPaymentModal = () => {
        if (_modal != null) {
            return _modal;
        }

        _modal = document.getElementById(ids.paymentModal);

        return _modal;
    }

    let reparented = false;

    const setPaymentUrl = (url: string) => {
        const iframe = document.getElementById(ids.paymentModalIFrame) as HTMLIFrameElement;
        iframe.src = url;
    }

    const setPaymentModalState = (state: string) => getPaymentModal().className = state;

    const showPaymentModal = (url: string) => {
        setPaymentUrl(url);

        setPaymentModalState(paymentState.loading);

        if (!reparented) {
            //Need to reparent due to pay@ bg opacity
            document.body.append(getPaymentModal());
            reparented = true;
        }
    }

    const tryAgain = () => {
        store.updateShowError(false);
        store.updateIsLoaded(false);

        showLoadingModal();
        getAccountBalance();
    }

    const PaymentModal = () => {
        //Note: need to add a contain wrapper as the Pay@ page sets the bg opacity so 
        //need to do a work around to make the whole page opaque
        return (
            <div id={ids.paymentModal}>
                <section id="payment-modal-loader-section">
                    <PleaseWait message="Connecting to payment gateway" isInModal={true} />
                </section>
                <section id="payment-modal-iframe-section">
                    <iframe id={ids.paymentModalIFrame} title="@pay" frameBorder="0" src="about:blank"></iframe>
                </section>
            </div>
        );
    }

    if (store.showError || !store.isLoaded) {
        return null;
    }

    const minAmountForWidthCalc = 1000;
    const amount = (store.amountToPay.amount < minAmountForWidthCalc
        ? minAmountForWidthCalc
        : store.amountToPay.amount).toFixed(2);

    const width = getTextWidth(amount, "bold 45px Robotica") + "px";
    const length = amount.toString().length + 1;

    const paymentMethodsCssName = `payment-methods selected-${store.method}`;

    const isValid = amountInput.isValid && store.isValid;

    const btnCssClassName = "btn " + (isValid ? "btn-primary" : "btn-black disabled");

    const updateAmountToPay = (e: any) => {
        let value = e.target.value as string;

        if (value.includes("e")) {
            value = value.replace("e", "");
            e.target.value = value;
        }

        amountInput.valueChangeHandler(e);
        store.updateAmountToPayValue(parseInt(e.target.value));
    }

    return (
        <>
            <section className="sa-payment" >
                <h4>
                    Amount to pay
                </h4>

                <p className="amount-to-pay">
                    {store.amountToPay.currencySymbol}
                    <input type="number"
                        size={length}
                        className="amount-to-pay"
                        value={amountInput.value}
                        disabled={!updateMode}
                        onChange={updateAmountToPay}
                        style={{ width: width }} />
                    <i className="icon icon-pencil" onClick={() => setUpdateMode(true)} />
                </p>

                <p>
                    Select a payment method below.
                </p>

                <ul className={paymentMethodsCssName} >
                    {paymentMethods.map(x => PaymentMethod(x))}
                </ul>

                <button className={btnCssClassName} onClick={payNow} disabled={!isValid}>Pay Now</button>

                <PaymentModal />
            </section>

            <i className="payment-logo" title="Powered by pay@ - Your Payment Partner"></i>
        </>
    );
});

export default Payment;
